/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
// Office staff days off (Paula 2026-10-20: Erika enters her own days off).
// mode="mine": the signed-in person adds and removes their own (My Hours).
// mode="all": admins see everyone's upcoming days off (Requests page).
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { todayET } from '../utils/et';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}
function fmtDate(d) {
  const [y, mo, da] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, mo - 1, da).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const card = { background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '12px 14px', marginBottom: 8 };
const input = { width: '100%', fontSize: 14, padding: '8px', border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' };

export default function StaffTimeOff({ mode = 'mine' }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ startDate: todayET(), endDate: '', partDay: false, fromTime: '', untilTime: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/payroll/staff-time-off' + (mode === 'all' ? '?all=1' : ''));
      setEntries(r.data.entries || []);
    } catch (e) { /* non-blocking */ }
  }, [mode]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      await api.post('/payroll/staff-time-off', {
        personName: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        fromTime: form.partDay ? form.fromTime : null,
        untilTime: form.partDay ? form.untilTime : null,
        reason: form.reason || null,
      });
      setOpen(false);
      setForm({ startDate: todayET(), endDate: '', partDay: false, fromTime: '', untilTime: '', reason: '' });
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this day off?')) return;
    try { await api.delete(`/payroll/staff-time-off/${id}`); load(); } catch (e) { alert('Could not remove'); }
  };

  if (mode === 'all' && entries.length === 0) return null;

  return (
    <div style={{ marginTop: mode === 'mine' ? 28 : 0, marginBottom: mode === 'all' ? 20 : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h2 style={{ fontSize: 14, fontWeight: 500, margin: 0, color: '#666' }}>{mode === 'mine' ? 'My days off' : 'Office staff days off'}</h2>
        {mode === 'mine' && !open && (
          <button onClick={() => setOpen(true)} style={{ fontSize: 13, background: '#185FA5', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer' }}>
            + Day off
          </button>
        )}
      </div>

      {open && (
        <div style={{ ...card, background: '#F7FAFD' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 11, color: '#888' }}>First day</label>
              <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={input} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#888' }}>Last day (if more than one)</label>
              <input type="date" value={form.endDate} min={form.startDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} style={input} />
            </div>
          </div>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input type="checkbox" checked={form.partDay} onChange={e => setForm(f => ({ ...f, partDay: e.target.checked }))} />
            Only part of the day
          </label>
          {form.partDay && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <label style={{ fontSize: 11, color: '#888' }}>Off from</label>
                <input type="time" value={form.fromTime} onChange={e => setForm(f => ({ ...f, fromTime: e.target.value }))} style={input} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#888' }}>Until</label>
                <input type="time" value={form.untilTime} onChange={e => setForm(f => ({ ...f, untilTime: e.target.value }))} style={input} />
              </div>
            </div>
          )}
          <input placeholder="Reason (optional)" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} style={{ ...input, marginBottom: 8 }} />
          {error && <div style={{ color: '#A32D2D', fontSize: 12, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving || !form.startDate} style={{ fontSize: 13, background: '#185FA5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setOpen(false); setError(''); }} style={{ fontSize: 13, background: 'white', border: '1px solid #ddd', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {entries.length === 0 && mode === 'mine' && !open && (
        <div style={{ ...card, color: '#999', fontSize: 13, textAlign: 'center' }}>No upcoming days off</div>
      )}
      {entries.map(e => (
        <div key={e.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              {mode === 'all' ? `${e.person_name || e.user_email} · ` : ''}{fmtDate(e.off_date)}
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              {e.from_time ? `Off ${fmtTime(e.from_time)} – ${fmtTime(e.until_time)}` : 'All day'}{e.reason ? ` · ${e.reason}` : ''}
            </div>
          </div>
          <button onClick={() => remove(e.id)} style={{ fontSize: 12, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
        </div>
      ))}
    </div>
  );
}
